import { useState, useRef, useEffect, useCallback } from 'react';
import throttle from 'lodash/throttle';
import { cn } from '@discover-lib/utils';
import { roundToClosest } from '@discover-lib/dataFormats';
import DevMenuButton from './button';

const STATE_KEY = 'ft-restaurant-store-dev-menu-position';
const WIDTH = 400;
const X_OFFSCREEN = -WIDTH + 17;

function setLocalStorage(title: string, position: { x: number; y: number }) {
  localStorage.setItem(`${STATE_KEY}-${title}`, JSON.stringify(position));
}

function getLocalStorage(title: string): { x: number; y: number } {
  if (typeof window === 'undefined') {
    return { x: X_OFFSCREEN, y: 0 };
  }

  const stored = localStorage.getItem(`${STATE_KEY}-${title}`);
  if (stored) {
    try {
      return JSON.parse(stored);
    } catch {
      // fallback to default position
    }
  }

  return { x: X_OFFSCREEN, y: 0 };
}

export type DevMenuRootProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> & {
  title: string;
};

export default function DevMenuRoot({ title, className, children, ...props }: DevMenuRootProps) {
  const [position, setPosition] = useState({ x: X_OFFSCREEN, y: 0 });

  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: X_OFFSCREEN, y: 0 });
  const elementRef = useRef<HTMLDivElement>(null);

  function updateState() {
    const pos = getLocalStorage(title);
    setPosition(pos);
  }

  function setState(newPosition: { x: number; y: number }) {
    setLocalStorage(title, newPosition);
    updateState();
  }

  // get state from local storage on mount
  useEffect(() => {
    updateState();
  }, []);

  const handleSetVisible = (visible: boolean) => {
    const newPosition = { x: visible ? Math.max(0, position.x) : X_OFFSCREEN, y: position.y };

    setState(newPosition);
  };

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  }, []);

  const handleMouseMove = throttle((e: MouseEvent) => {
    if (isDragging) {
      const newPosition = {
        x: roundToClosest(e.clientX - dragOffset.x, 10), // snap to a 10 wide "grid"
        y: roundToClosest(e.clientY - dragOffset.y, 10),
      };

      setState(newPosition);
    }
  }, 100);

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  return (
    <div
      ref={elementRef}
      className={cn(
        'prose fixed flex max-w-none flex-col gap-2 rounded bg-blue-50 p-4 pr-8 text-xs shadow-md',
        className
      )}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        zIndex: 9999,
        width: `${WIDTH}px`,
        maxWidth: `${WIDTH}px`,
      }}
      {...props}>
      {/* Drag Handle */}
      <div
        className={cn(
          'absolute top-1 flex h-4 w-12 items-center justify-center self-center rounded-full bg-gray-200 transition-colors hover:bg-gray-300',
          isDragging ? '!cursor-grabbing' : '!cursor-grab'
        )}
        onMouseDown={handleMouseDown}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
          }
        }}
        title="Drag to move"
        aria-label="Drag handle to move menu">
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="currentColor"
          className="text-white"
          aria-hidden="true">
          <path d="M4 9a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM12 9a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM12 17a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM20 9a2 2 0 1 1 0-4 2 2 0 0 1 0 4zM20 17a2 2 0 1 1 0-4 2 2 0 0 1 0 4z" />
        </svg>
      </div>
      {children}
      <DevMenuButton visible={position.x >= 0} setVisible={handleSetVisible} />
    </div>
  );
}
